import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  CheckSquare, Clock, FileClock, Landmark, CheckCircle2,
  AlertOctagon, PauseCircle, CreditCard, HelpCircle, ShieldAlert,
  Sparkles, Mail, Phone, ChevronRight, History, ArrowRight,
  FileText, Ban, AlertCircle, RefreshCw
} from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { Reveal } from "@/components/landing/Reveal";
import { AmbientOrbs } from "@/components/landing/Ambient";
import { ScrollProgress, WhatsAppFab } from "@/components/landing/Floating";
import SEO from "@/components/SEO";
import { EMAIL, PHONE } from "@/lib/data";

const SECTIONS = [
  { id: "confirmation", title: "1. Service Confirmation", icon: CheckSquare },
  { id: "cancel-before", title: "2. Cancellation Before Work Commences", icon: Clock },
  { id: "cancel-after", title: "3. Cancellation After Work Commences", icon: FileClock },
  { id: "govt-fees", title: "4. Government & Third-Party Fees", icon: Landmark },
  { id: "completed", title: "5. Services Already Completed", icon: CheckCircle2 },
  { id: "govt-rejection", title: "6. Government Rejection", icon: AlertOctagon },
  { id: "client-delay", title: "7. Client Delay", icon: PauseCircle },
  { id: "processing", title: "8. Refund Processing", icon: CreditCard },
  { id: "service-deficiency", title: "9. Service Deficiency", icon: HelpCircle },
  { id: "no-guarantee", title: "10. No Guaranteed Outcome", icon: ShieldAlert },
  { id: "exceptional", title: "11. Exceptional Circumstances", icon: Sparkles },
  { id: "contact", title: "12. Contact for Refunds", icon: Mail },
];

export default function RefundPolicy() {
  const [activeSection, setActiveSection] = useState("confirmation");

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 140;
      for (const section of SECTIONS) {
        const el = document.getElementById(section.id);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const navOffset = 100;
      const top = el.getBoundingClientRect().top + window.pageYOffset - navOffset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <div className="relative min-h-screen bg-slate-50 font-sans text-navy-900 antialiased" data-testid="refund-policy-page">
      <SEO
        title="Refund, Cancellation & Service Policy | Biz360 Compliance"
        description="Official Refund, Cancellation & Service Policy for Biz360 Compliance. Learn our terms regarding service confirmation, cancellation stages, government fees, and refund processing."
        canonical="/refund-policy"
        breadcrumbs={[{ label: "Refund & Cancellation Policy", href: "/refund-policy" }]}
      />
      <ScrollProgress />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-[rgb(238,242,250)] pt-32 pb-14 md:pt-40 md:pb-16 border-b border-slate-200">
          <AmbientOrbs />
          <div className="relative mx-auto max-w-5xl px-5 lg:px-8">
            <Reveal>
              <div className="mb-6 flex flex-wrap items-center gap-1.5 text-xs font-medium text-slate-500" data-testid="refund-breadcrumb">
                <Link to="/" className="hover:text-navy-800 transition-colors">Home</Link>
                <ChevronRight className="h-3 w-3" />
                <span className="text-navy-700">Refund, Cancellation & Service Policy</span>
              </div>
            </Reveal>

            <div className="text-center">
              <Reveal delay={0.05}>
                <span className="inline-flex items-center rounded-full border border-gold-300/60 bg-gold-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-gold-700">
                  Service Terms & Fair Practices
                </span>
              </Reveal>

              <Reveal delay={0.1}>
                <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-navy-900 md:text-5xl">
                  REFUND, CANCELLATION & SERVICE POLICY
                </h1>
              </Reveal>

              <Reveal delay={0.15}>
                <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-white/80 px-4 py-1.5 text-xs font-medium text-slate-600 shadow-sm border border-slate-200">
                  <History className="h-3.5 w-3.5 text-gold-600" />
                  <span>Last Updated: <strong className="text-navy-900">01/09/2026</strong></span>
                </div>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="mx-auto mt-6 max-w-3xl rounded-2xl border border-navy-900/10 bg-white/90 p-6 text-left shadow-sm backdrop-blur-sm">
                  <p className="text-base font-semibold text-navy-900">
                    Biz360 Compliance provides professional, advisory, documentation, accounting, compliance and business-support services.
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    Because many services involve work being performed specifically for an individual client, refund and cancellation treatment depends upon the stage of work and the nature of the service.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Content Container */}
        <section className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            
            {/* Quick Navigation Sticky Sidebar */}
            <aside className="hidden lg:col-span-4 lg:block">
              <div className="sticky top-28 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm max-h-[calc(100vh-140px)] overflow-y-auto">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 px-2">
                  Table of Contents
                </h3>
                <nav className="space-y-1">
                  {SECTIONS.map((sec) => {
                    const SIcon = sec.icon;
                    const isActive = activeSection === sec.id;
                    return (
                      <button
                        key={sec.id}
                        onClick={() => scrollToSection(sec.id)}
                        className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-xs font-medium transition-all ${
                          isActive
                            ? "bg-navy-900 text-white shadow-sm"
                            : "text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                        }`}
                      >
                        <SIcon className={`h-4 w-4 shrink-0 ${isActive ? "text-gold-400" : "text-slate-400"}`} />
                        <span className="truncate">{sec.title}</span>
                      </button>
                    );
                  })}
                </nav>

                <div className="mt-6 border-t border-slate-100 pt-5">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 px-2">
                    Related Legal Policies
                  </h4>
                  <div className="space-y-1.5 text-xs font-medium">
                    <Link
                      to="/terms"
                      className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                    >
                      <span>Terms & Conditions</span>
                      <ArrowRight className="h-3 w-3 text-slate-400" />
                    </Link>
                    <Link
                      to="/privacy-policy"
                      className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                    >
                      <span>Privacy Policy</span>
                      <ArrowRight className="h-3 w-3 text-slate-400" />
                    </Link>
                    <Link
                      to="/data-security-policy"
                      className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                    >
                      <span>Data Security Policy</span>
                      <ArrowRight className="h-3 w-3 text-slate-400" />
                    </Link>
                    <Link
                      to="/data-retention-policy"
                      className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                    >
                      <span>Data Retention Policy</span>
                      <ArrowRight className="h-3 w-3 text-slate-400" />
                    </Link>
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Policy Content */}
            <div className="space-y-8 lg:col-span-8">

              {/* 1. Service Confirmation */}
              <article id="confirmation" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <CheckSquare className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">1. Service Confirmation</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  A service is considered confirmed when:
                </p>
                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "The client accepts the quotation/proposal;",
                    "Required information is provided;",
                    "Payment is received, where applicable; or",
                    "Biz360 otherwise confirms commencement of the engagement."
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-2.5 rounded-lg bg-slate-50 p-3 border border-slate-100">
                      <span className="mt-1 h-2 w-2 rounded-full bg-emerald-600 shrink-0" />
                      <span className="text-xs font-semibold text-slate-800">{item}</span>
                    </div>
                  ))}
                </div>
              </article>

              {/* 2. Cancellation Before Work Commences */}
              <article id="cancel-before" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Clock className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">2. Cancellation Before Work Commences</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Where a client requests cancellation before substantive work has commenced, Biz360 may consider a refund of the amount received after deducting:
                </p>
                <ul className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Applicable payment gateway charges;",
                    "Third-party charges already incurred;",
                    "Administrative/documentation costs; and",
                    "Any other amount already contractually committed."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-gold-600 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3.5 text-xs font-medium text-slate-600">
                  The final refund will depend on the specific service and agreed terms.
                </div>
              </article>

              {/* 3. Cancellation After Work Commences */}
              <article id="cancel-after" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <FileClock className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">3. Cancellation After Work Commences</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Once substantive work has commenced, the amount refundable, if any, will depend upon:
                </p>
                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Work already completed;",
                    "Time and resources spent;",
                    "Documents prepared;",
                    "Government/third-party charges;",
                    "Applications already submitted; and",
                    "The agreed scope of work."
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2 rounded-lg bg-slate-50 p-2.5 border border-slate-100 text-xs font-medium text-slate-800">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs font-semibold text-rose-900 bg-rose-50 border border-rose-200 p-3 rounded-xl">
                  Fees attributable to work already performed may not be refundable.
                </p>
              </article>

              {/* 4. Government & Third-Party Fees */}
              <article id="govt-fees" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Landmark className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">4. Government & Third-Party Fees</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Government fees, statutory charges, application fees, payment gateway charges and third-party fees are generally non-refundable once paid or incurred, subject to the applicable authority's rules.
                </p>
              </article>

              {/* 5. Services Already Completed */}
              <article id="completed" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <CheckCircle2 className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">5. Services Already Completed</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  No refund will ordinarily be available merely because a client changes their mind after a service has been completed.
                </p>
                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/70 p-4 text-sm text-slate-700 leading-relaxed">
                  For example, where an agreed application, filing, registration or documentation assignment has been completed and delivered/submitted, the applicable professional/service fee is generally non-refundable, subject to applicable law and the specific engagement terms.
                </div>
              </article>

              {/* 6. Government Rejection */}
              <article id="govt-rejection" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <AlertOctagon className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">6. Government Rejection</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Where an application, registration, refund, subsidy, loan, licence or other outcome is rejected by a government authority, bank or third party for reasons outside Biz360's control, the professional fee for work already performed will generally remain payable.
                </p>
                <p className="mt-3 text-xs font-semibold text-slate-600 bg-slate-100 p-3 rounded-xl">
                  Where appropriate, additional assistance may be offered under a separate engagement.
                </p>
              </article>

              {/* 7. Client Delay */}
              <article id="client-delay" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <PauseCircle className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">7. Client Delay</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Where a service cannot be completed because the client:
                </p>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  {[
                    "Does not provide required documents;",
                    "Provides incorrect information;",
                    "Does not provide approvals;",
                    "Does not complete required verification; or",
                    "Remains unresponsive,"
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-amber-600 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/50 p-4">
                  <p className="text-xs font-bold text-amber-950">
                    The engagement may be placed on hold. Fees for work already performed may remain payable.
                  </p>
                </div>
              </article>

              {/* 8. Refund Processing */}
              <article id="processing" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <CreditCard className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">8. Refund Processing</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Approved refunds will ordinarily be processed through the original payment method, wherever technically possible.
                </p>
                <p className="mt-3 text-xs font-semibold text-slate-600 bg-slate-100 p-3 rounded-xl">
                  Processing time may depend upon the payment gateway or financial institution.
                </p>
              </article>

              {/* 9. Service Deficiency */}
              <article id="service-deficiency" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <HelpCircle className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">9. Service Deficiency</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  If you believe that a service has not been delivered according to the agreed scope, please contact us promptly with the relevant details.
                </p>
                <p className="mt-3 text-sm font-semibold text-navy-900">
                  We will review the matter and, where appropriate, attempt to:
                </p>
                <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-3 text-sm text-slate-700">
                  {[
                    "Correct the deficiency;",
                    "Complete the pending scope; or",
                    "Provide another appropriate resolution."
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2 rounded-lg bg-slate-50 p-3 border border-slate-200/70 text-xs font-medium text-slate-800">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-600 shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </article>

              {/* 10. No Guaranteed Outcome */}
              <article id="no-guarantee" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <ShieldAlert className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">10. No Guaranteed Outcome</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Payment for professional services does not guarantee:
                </p>
                <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 text-sm text-slate-700">
                  {[
                    "Government approval",
                    "Tax refund",
                    "Loan approval",
                    "Subsidy",
                    "Registration",
                    "Licence",
                    "Certificate",
                    "Any other outcome controlled by a third party"
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2 rounded-lg bg-slate-50 p-2.5 border border-slate-200/70 text-xs font-semibold text-slate-800">
                      <span className="h-1.5 w-1.5 rounded-full bg-gold-600 shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </article>

              {/* 11. Exceptional Circumstances */}
              <article id="exceptional" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Sparkles className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">11. Exceptional Circumstances</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Refund or cancellation requests arising from exceptional circumstances will be reviewed on a case-by-case basis, subject to applicable law and the terms of the relevant engagement.
                </p>
              </article>

              {/* 12. Contact for Refunds */}
              <article id="contact" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Mail className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">12. Contact for Refunds</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Refund or cancellation requests may be submitted to:
                </p>

                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <a
                    href={`mailto:${EMAIL}`}
                    className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 transition-colors hover:border-gold-500 hover:bg-gold-50/20"
                  >
                    <Mail className="h-5 w-5 text-gold-600" />
                    <div>
                      <span className="block text-xs font-bold uppercase tracking-wider text-slate-400">Email</span>
                      <span className="text-sm font-semibold text-navy-900">{EMAIL}</span>
                    </div>
                  </a>

                  <a
                    href={`tel:${PHONE.replace(/\s+/g, "")}`}
                    className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 transition-colors hover:border-gold-500 hover:bg-gold-50/20"
                  >
                    <Phone className="h-5 w-5 text-gold-600" />
                    <div>
                      <span className="block text-xs font-bold uppercase tracking-wider text-slate-400">Phone</span>
                      <span className="text-sm font-semibold text-navy-900">{PHONE}</span>
                    </div>
                  </a>
                </div>

                <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50/70 p-5">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-navy-900">
                    Please include the following information:
                  </h4>
                  <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 text-xs font-medium text-slate-700">
                    {[
                      "Client name",
                      "Invoice / reference number",
                      "Service name",
                      "Date of payment",
                      "Reason for the request"
                    ].map((item) => (
                      <li key={item} className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-600 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>

            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppFab />
    </div>
  );
}
